/* ─────────────────────────────────────────────────────────────────────────
 * Evidence vocabulary.
 *
 * STATUS: NOT CONFIRMED BY THE GEOLOGY TEAM. `VOCABULARY_CONFIRMED` is false
 * and every surface that renders these tags must say so — the desk shows a
 * banner, the capture screen shows a line. When the team signs off, flip the
 * flag and the banners disappear.
 *
 * Provenance of each list is marked. The alteration terms are DERIVED from the
 * facies model in PRD §5.7 rather than enumerated by anyone:
 *
 *   "footwall alteration pipe (chlorite-sericite) → stringer/stockwork zone →
 *    massive sulfide horizon → exhalative chemical sediment cap
 *    (chert/jasper/barite) → hanging wall"
 *
 * and the primary field indicator, verbatim: "gossan/rusty-rock (iron oxide
 * staining)".
 *
 * The PRD references "the evidence vocabulary" when describing assignment
 * expected-tags but never enumerates it. In production this table is
 * server-managed and editable from the desk admin screen (Addendum A4.8);
 * these values are only the seed.
 * ───────────────────────────────────────────────────────────────────────── */

export const VOCABULARY_CONFIRMED = false;

export const VOCABULARY_NOTICE =
  'Tag list is a draft derived from the PRD facies model — not yet confirmed by the geology team.';

export type TagProvenance = 'prd-derived' | 'placeholder';

export interface TagGroup {
  id: string;
  label: string;
  provenance: TagProvenance;
  /** Rendered under the group heading where the provenance needs explaining. */
  note?: string;
  tags: string[];
}

export const TAG_GROUPS: TagGroup[] = [
  {
    id: 'alteration',
    label: 'Alteration',
    provenance: 'prd-derived',
    note: 'From the PRD facies model. Ordering follows the sequence, footwall first.',
    // Every term below appears in PRD §5.7. Ordered down-section so the chips
    // read the way the model does, which is how a geologist thinks about them.
    tags: ['gossan', 'chlorite', 'sericite', 'stringer', 'stockwork', 'chert', 'jasper', 'barite'],
  },
  {
    id: 'rock_type',
    label: 'Rock type',
    provenance: 'placeholder',
    note: 'No source in the PRD. Deliberately minimal — expect the team to replace this outright.',
    // SWAP — kept short on purpose. A long invented list reads as authoritative
    // and would quietly become the schema.
    tags: ['rhyolite', 'andesite', 'basalt', 'tuff', 'sediment'],
  },
  {
    id: 'texture',
    label: 'Texture',
    provenance: 'placeholder',
    note: 'PRD names texture_type as a field but gives no vocabulary.',
    // SWAP
    tags: ['massive', 'brecciated', 'laminated', 'vesicular', 'foliated'],
  },
];

export const ALL_TAGS = TAG_GROUPS.flatMap((g) => g.tags);

export const tagGroup = (tag: string): TagGroup | undefined =>
  TAG_GROUPS.find((g) => g.tags.includes(tag));

/** PRD §5.2, verbatim: "fault / fold / contact / joint set / alteration zone". */
export const FEATURE_TYPES = [
  { id: 'fault', label: 'Fault' },
  { id: 'fold', label: 'Fold' },
  { id: 'contact', label: 'Contact' },
  { id: 'joint_set', label: 'Joint set' },
  { id: 'alteration_zone', label: 'Alteration zone' },
] as const;

/** PRD: confidence high / medium / low. */
export const CONFIDENCE_LEVELS = [
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
] as const;

/** SWAP — the PRD does not enumerate offset sense. */
export const OFFSET_SENSES = ['normal', 'reverse', 'dextral', 'sinistral', 'unknown'];

export const DIP_DIRECTIONS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
